import { useEffect, useRef, useState } from "react";
import type { ProductData } from "@/mocks/products";
import SecretDoor from "./SecretDoor";

interface Props {
  product: ProductData;
}

export default function ProductHero({ product }: Props) {
  const [visible, setVisible] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const [r, g, b] = hexToRgb(product.accentColor);
    const particles: {
      x: number; y: number; r: number;
      dx: number; dy: number; alpha: number; pulse: number;
    }[] = [];

    for (let i = 0; i < 40; i++) {
      particles.push({
        x: Math.random() * (canvas.width || 1920),
        y: Math.random() * (canvas.height || 1080),
        r: Math.random() * 1.5 + 0.3,
        dx: (Math.random() - 0.5) * 0.12,
        dy: (Math.random() - 0.5) * 0.12,
        alpha: Math.random() * 0.35 + 0.05,
        pulse: Math.random() * Math.PI * 2,
      });
    }

    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.pulse += 0.007;
        const a = p.alpha * (0.5 + 0.5 * Math.sin(p.pulse));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      animId = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, [product.accentColor]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#060508]">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${product.heroImage})`,
          filter: "brightness(0.28) saturate(0.6)",
          transform: "scale(1.05)",
        }}
      />

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#060508]/70 via-transparent to-[#060508]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#060508]/50 via-transparent to-[#060508]/50" />

      {/* Glow orb */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${product.glowColor} 0%, transparent 70%)`,
          filter: "blur(60px)",
        }}
      />

      {/* Particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />

      {/* Top line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-20 bg-gradient-to-b from-transparent to-[#c9a84c]/20" />

      {/* Content */}
      <div
        className="relative z-10 text-center px-6 w-full max-w-4xl mx-auto"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(24px)",
          transition: "opacity 2s ease, transform 2s ease",
        }}
      >
        {/* Kingdom label */}
        <div
          className="text-xs tracking-[0.6em] uppercase mb-10"
          style={{ color: `${product.accentColor}60`, letterSpacing: "0.6em" }}
        >
          {product.kingdom}
        </div>

        {/* Product name */}
        <h1
          className="text-6xl md:text-8xl lg:text-9xl font-light text-white mb-6 leading-none"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            letterSpacing: "0.04em",
            textShadow: `0 0 120px ${product.glowColor}`,
          }}
        >
          {product.name}
        </h1>

        {/* Divider */}
        <div
          className="w-12 h-px mx-auto mb-8"
          style={{ background: `${product.accentColor}50` }}
        />

        {/* Hero phrase */}
        <p
          className="text-white/60 text-xl md:text-2xl font-light leading-relaxed mb-16 whitespace-pre-line"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            letterSpacing: "0.05em",
          }}
        >
          {product.heroPhrase}
        </p>

        {/* Secret Door CTA */}
        <SecretDoor product={product} />
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        style={{
          opacity: visible ? 0.35 : 0,
          transition: "opacity 2s ease 2s",
        }}
      >
        <div className="w-px h-14 bg-gradient-to-b from-[#c9a84c]/50 to-transparent animate-pulse" />
      </div>
    </section>
  );
}

function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [201, 168, 76];
}
